import React, { useState } from 'react';
import { Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * On-device OCR's tap-to-fill panel, task: Capacitor + on-device OCR
 * before FS/TEC (2026-09-08). Shown only offline, on the native app,
 * when ML Kit found at least one text block on the badge/card photo
 * BadgeScanner.jsx already captured (see onDeviceOcr.js).
 *
 * Deliberately one tap per assignment, not two: each recognized block is
 * a label with a row of field targets directly under it, not a block a
 * rep taps first and then picks a field from a second menu. The owner's
 * own ask was whether tap-to-fill is actually faster than typing, and a
 * second tap per field just to open a picker would have made that a much
 * closer call, especially for a short, familiar field like a first name.
 * One tap per field keeps this strictly additive: a rep who finds a given
 * block not worth tapping just types that one field instead, exactly as
 * they could before this existed.
 *
 * Every field stays independently editable by hand afterward, same as
 * always: this only ever calls onAssign, which NewMeeting.jsx's
 * handleBadgeData already merges into formData the same non-destructive
 * way the online scan's partial results do, so a tap here is never a
 * bigger commitment than typing over it a second later.
 */
const FIELD_TARGETS = [
  ['first_name', 'First'],
  ['last_name', 'Last'],
  ['company', 'Company'],
  ['title', 'Title'],
  ['email', 'Email'],
  ['phone', 'Phone'],
];

export default function TapToFillBlocks({ blocks, onAssign, onDismiss }) {
  const [assigned, setAssigned] = useState({}); // field -> the block text last assigned to it

  const handleTap = (field, text) => {
    setAssigned((prev) => ({ ...prev, [field]: text }));
    onAssign(field, text);
  };

  if (!blocks || blocks.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-secondary/30 border border-border rounded-xl p-3 space-y-2.5"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-foreground">
          Tap the text below, then tap which field it is
        </p>
        <button type="button" onClick={onDismiss} className="p-1 -mr-1 text-muted-foreground hover:text-foreground">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="space-y-2">
        <AnimatePresence initial={false}>
          {blocks.map((block, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-card rounded-lg p-2 border border-border/60"
            >
              <p className="text-sm text-foreground font-medium break-words mb-1.5">{block.text}</p>
              <div className="flex flex-wrap gap-1.5">
                {FIELD_TARGETS.map(([field, label]) => {
                  const isActive = assigned[field] === block.text;
                  return (
                    <button
                      key={field}
                      type="button"
                      onClick={() => handleTap(field, block.text)}
                      className={`text-[11px] font-medium px-2 py-1 rounded-full border transition-colors flex items-center gap-1 ${
                        isActive
                          ? 'bg-accent text-accent-foreground border-accent'
                          : 'bg-background text-muted-foreground border-border hover:border-accent/50 hover:text-foreground'
                      }`}
                    >
                      {isActive && <Check className="w-3 h-3" />}
                      {label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
